(() => {
  const roots = document.querySelectorAll('[data-ep-assisted-order]');
  if (!roots.length) return;

  const loadScript = (src, key) => {
    const existing = document.querySelector(`script[data-ep-external="${key}"]`);
    if (existing?.dataset.loaded === 'true') return Promise.resolve();

    if (existing) {
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.epExternal = key;
      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  };

  const ensureUploadcare = async (publicKey) => {
    if (!window.jQuery) {
      await loadScript('https://code.jquery.com/jquery-3.7.1.min.js', 'jquery');
    }

    window.UPLOADCARE_PUBLIC_KEY = publicKey;
    window.UPLOADCARE_LOCALE = 'fr';

    if (!window.uploadcare) {
      await loadScript('https://ucarecdn.com/libs/widget/3.x/uploadcare.min.js', 'uploadcare');
    }

    if (!window.uploadcare) throw new Error('Uploadcare indisponible');
    return window.uploadcare;
  };

  roots.forEach((root) => {
    const productInfo = root.closest('product-info');
    const configuratorCardCopy = root.querySelector('[data-ep-configurator-card-copy]');
    const choice = root.querySelector('[data-ep-assisted-choice]');
    const assistedCardCopy = root.querySelector('[data-ep-assisted-card-copy]');
    const triggerSlot = root.querySelector('[data-ep-assisted-trigger-slot]');
    const trigger = root.querySelector('[data-ep-assisted-trigger]');
    const panel = root.querySelector('[data-ep-assisted-panel]');
    const uploaderInput = root.querySelector('[data-ep-assisted-uploader]');
    const uploadButton = root.querySelector('[data-ep-assisted-upload]');
    const fileStatus = root.querySelector('[data-ep-assisted-file]');
    const commentInput = root.querySelector('[data-ep-assisted-comment]');
    const submitButton = root.querySelector('[data-ep-assisted-submit]');
    const message = root.querySelector('[data-ep-assisted-message]');
    const publicKey = root.dataset.uploadcarePublicKey;

    if (
      !productInfo ||
      !configuratorCardCopy ||
      !choice ||
      !assistedCardCopy ||
      !triggerSlot ||
      !trigger ||
      !panel ||
      !uploaderInput ||
      !uploadButton ||
      !submitButton
    ) {
      return;
    }

    let logoUrl = '';
    let logoName = '';
    let widget = null;
    let widgetPromise = null;
    let placementAttempts = 0;
    const mobileLayout = window.matchMedia('(max-width: 749px)');

    const setMessage = (text, type = '') => {
      if (!message) return;
      message.textContent = text;
      message.classList.toggle('is-error', type === 'error');
      message.classList.toggle('is-success', type === 'success');
    };

    const setFileStatus = (text, ready = false) => {
      if (!fileStatus) return;
      fileStatus.textContent = text;
      fileStatus.classList.toggle('is-ready', ready);
    };

    const placeTrigger = () => {
      const ctaRow = productInfo.querySelector('.ep-fancy-cta-row');
      if (!ctaRow) {
        placementAttempts += 1;
        if (placementAttempts < 40) window.setTimeout(placeTrigger, 50);
        return;
      }

      const configurator = ctaRow.querySelector('.ep-fancy-cta-configurator');
      if (!configurator) return;

      if (configuratorCardCopy.parentElement !== configurator) configurator.prepend(configuratorCardCopy);
      if (assistedCardCopy.parentElement !== triggerSlot) triggerSlot.prepend(assistedCardCopy);
      if (triggerSlot.parentElement !== ctaRow) ctaRow.insertBefore(triggerSlot, configurator);
      if (choice.parentElement !== ctaRow) triggerSlot.insertAdjacentElement('afterend', choice);
      if (root.previousElementSibling !== ctaRow) ctaRow.insertAdjacentElement('afterend', root);
    };

    const initializeUploader = () => {
      if (widget) return Promise.resolve(widget);
      if (widgetPromise) return widgetPromise;

      widgetPromise = ensureUploadcare(publicKey)
        .then((uploadcare) => {
          widget = uploadcare.Widget(uploaderInput);

          widget.onChange((fileInfo) => {
            logoUrl = '';
            logoName = '';
            setMessage('');
            setFileStatus(fileInfo ? 'Téléversement en cours…' : 'Aucun fichier sélectionné');

            if (fileInfo && typeof fileInfo.fail === 'function') {
              fileInfo.fail(() => {
                setFileStatus('Le téléversement a échoué');
                setMessage('Impossible de transmettre ce fichier. Réessayez avec un autre format.', 'error');
              });
            }
          });

          widget.onUploadComplete((fileInfo) => {
            logoUrl = fileInfo?.cdnUrl || '';
            logoName = fileInfo?.originalFilename || fileInfo?.name || 'Logo client';

            if (!logoUrl) {
              setFileStatus('Le téléversement a échoué');
              setMessage('Impossible de récupérer le fichier transmis.', 'error');
              return;
            }

            setFileStatus(logoName, true);
            uploadButton.textContent = 'Remplacer le fichier';
            setMessage('Fichier prêt à être ajouté à votre commande.', 'success');
          });

          return widget;
        })
        .catch((error) => {
          console.error(error);
          widgetPromise = null;
          setFileStatus('Outil de téléversement indisponible');
          setMessage('Le dépôt de fichier ne s’est pas chargé. Réessayez dans quelques instants.', 'error');
          throw error;
        });

      return widgetPromise;
    };

    const setPanelOpen = (open, prepareUploader = true) => {
      root.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      panel.toggleAttribute('inert', !open);

      if (open && prepareUploader) initializeUploader().catch(() => {});
    };

    const addToCart = async () => {
      setMessage('');

      if (!logoUrl) {
        setMessage('Ajoutez votre logo ou une photo avant de continuer.', 'error');
        uploadButton.focus();
        return;
      }

      const productFormElement = productInfo.querySelector('product-form');
      const productForm = productFormElement?.querySelector('form');
      const variantInput = productForm?.querySelector('[name="id"]');
      const quantityInput = productInfo.querySelector('input.quantity__input, input[name="quantity"]');
      const nativeSubmit = productFormElement?.querySelector('[type="submit"]');
      const variantId = variantInput?.value;
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);

      if (!variantId || variantInput.disabled || nativeSubmit?.disabled) {
        setMessage('Cette combinaison n’est pas disponible. Vérifiez vos options avant de continuer.', 'error');
        return;
      }

      const spinner = submitButton.querySelector('.loading__spinner');
      submitButton.disabled = true;
      submitButton.classList.add('loading');
      spinner?.classList.remove('hidden');
      setMessage('Ajout au panier en cours…');

      try {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', String(quantity));
        formData.append('properties[Accompagnement]', 'Maquette préparée par FaciPack');
        formData.append('properties[Logo]', 'Fichier transmis');
        formData.append('properties[_ep_logo_url]', logoUrl);
        formData.append('properties[_ep_logo_name]', logoName);
        formData.append('properties[_ep_assisted]', 'true');

        const comment = commentInput?.value.trim();
        if (comment) formData.append('properties[Instructions]', comment);

        if (cart) {
          formData.append(
            'sections',
            cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(',')
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(submitButton);
        }

        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(window.routes.cart_add_url, config);
        const cartData = await response.json();

        if (!response.ok || cartData.status) {
          throw new Error(cartData.description || cartData.message || 'Impossible d’ajouter cet article au panier.');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ep-assisted-order',
            productVariantId: variantId,
            cartData,
          });
        }

        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(cartData);
        } else {
          window.location = window.routes.cart_url;
          return;
        }

        setMessage('Logo reçu. L’article a été ajouté au panier.', 'success');
      } catch (error) {
        console.error(error);
        setMessage(error.message || 'Une erreur est survenue pendant l’ajout au panier.', 'error');
      } finally {
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    };

    trigger.addEventListener('click', () => {
      setPanelOpen(trigger.getAttribute('aria-expanded') !== 'true');
    });

    uploadButton.addEventListener('click', async () => {
      setMessage('Préparation du dépôt de fichier…');
      try {
        const uploader = await initializeUploader();
        setMessage('');
        uploader.openDialog();
      } catch (error) {
        uploadButton.focus();
      }
    });

    submitButton.addEventListener('click', addToCart);
    placeTrigger();
    const syncMobileLayout = () => setPanelOpen(mobileLayout.matches, false);
    mobileLayout.addEventListener('change', syncMobileLayout);
    syncMobileLayout();
  });
})();
